import { AxiosError } from "axios";
import React, { useState, useEffect } from "react";
import { HiMail, HiLockClosed } from "react-icons/hi";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Input, Card } from "../../components/ui";
import { ROUTES } from "../../constants/routes";
import { useAuthStore } from "../../store/authStore";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated && location.pathname === ROUTES.LOGIN) {
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
  }, [isAuthenticated, navigate, location.pathname]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);

    try {
      await login(email, password);
    } catch (error) {
      console.error("Login failed:", error);

      let message = "로그인에 실패했습니다. 다시 시도해주세요.";

      if (error && typeof error === "object" && "isAxiosError" in error) {
        const axiosError = error as AxiosError;

        if (axiosError.response) {
          const status = axiosError.response.status;
          const url = axiosError.config?.url || "";

          if (status === 401) {
            message = url.includes("/auth/me")
              ? "인증 확인에 실패했습니다. 쿠키 설정을 확인해주세요."
              : "이메일 또는 비밀번호가 올바르지 않습니다.";
          } else if (status === 404) {
            message = url.includes("/auth/me")
              ? "인증 확인 엔드포인트를 찾을 수 없습니다. 서버 설정을 확인해주세요."
              : "서버를 찾을 수 없습니다. 서버 URL을 확인해주세요.";
          } else if (status >= 500) {
            message = "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
          } else {
            message = `오류가 발생했습니다. (${status})`;
          }
        } else if (axiosError.request) {
          message = "서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.";
        } else {
          message = "요청을 보낼 수 없습니다.";
        }
      } else if (error instanceof Error) {
        message = error.message || "로그인에 실패했습니다. 다시 시도해주세요.";
      }

      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md animate-slide-up">
        <div className="text-center mb-8">
          <img 
            src="/logo.png" 
            alt="Lumos Admin Logo" 
            className="w-12 h-12 mx-auto mb-4 object-contain"
          />
          <h1 className="text-2xl font-bold text-slate-900">
            관리자 로그인
          </h1>
          <p className="text-slate-500 mt-2">
            시스템에 접속하려면 계정 정보를 입력하세요
          </p>
        </div>

        <Card className="shadow-xl border-slate-100">
          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <Input
              id="email"
              type="email"
              label="이메일"
              placeholder="admin@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              leftIcon={<HiMail />}
            />

            <Input
              id="password"
              type="password"
              label="비밀번호"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              leftIcon={<HiLockClosed />}
            />

            <div className="flex justify-between items-center text-sm">
              <label className="flex items-center gap-2 text-slate-600 cursor-pointer hover:text-slate-900 transition-colors">
                <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span>로그인 상태 유지</span>
              </label>
              <button
                type="button"
                className="text-blue-600 hover:text-blue-700 hover:underline font-medium bg-transparent border-none cursor-pointer p-0"
                onClick={() => alert("비밀번호 찾기 기능은 준비 중입니다.")}
              >
                비밀번호 찾기
              </button>
            </div>

            <Button
              type="submit"
              fullWidth
              size="lg"
              isLoading={isLoading}
              className="mt-2"
            >
              로그인
            </Button>
          </form>
        </Card>
        
        <p className="text-center mt-8 text-sm text-slate-400">
          © 2024 Lumos Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
